import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';
import './TenantManagement.css';

const TenantManagement = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    subdomain: '',
    // Firma Yönetici Bilgileri
    admin_username: '',
    admin_email: '',
    admin_password: '',
    admin_password_confirm: '',
    // Firma İletişim Bilgileri
    company_phone: '',
    company_address: '',
    tax_number: '',
    // B2B ve B2C Domain'leri
    b2b_domain: '',
    b2c_domain: '',
    // Servis ve Portal Seçimleri
    enabled_portals: [],
    enabled_services: []
  });

  // Portal ve servis listesi
  const [availablePortals, setAvailablePortals] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);

  useEffect(() => {
    // Sadece kimlik doğrulama tamamlandığında ve kullanıcı giriş yapmışsa veri yükle
    if (!authLoading && isAuthenticated) {
      loadTenants();
      loadAvailablePortalsAndServices();
    }
  }, [authLoading, isAuthenticated]);

  const loadAvailablePortalsAndServices = async () => {
    try {
      console.log('DEBUG: Loading available portals and services...');
      console.log('DEBUG: Current formData.enabled_portals before loading:', formData.enabled_portals);
      console.log('DEBUG: Current formData.enabled_services before loading:', formData.enabled_services);
      
      const [portals, services] = await Promise.all([
        apiService.getAvailablePortals(),
        apiService.getAvailableServices()
      ]);
      console.log('DEBUG: Portals loaded:', portals);
      console.log('DEBUG: Services loaded:', services);
      
      setAvailablePortals(portals);
      setAvailableServices(services);
      
      console.log('DEBUG: Available portals and services set');
      console.log('DEBUG: Current formData.enabled_portals after loading:', formData.enabled_portals);
      console.log('DEBUG: Current formData.enabled_services after loading:', formData.enabled_services);
    } catch (error) {
      console.error('Error loading portals and services:', error);
    }
  };

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await apiService.getTenants();
      setTenants(data);
    } catch (error) {
      console.error('Error loading tenants:', error);
      setError('Firmalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    try {
      console.log('DEBUG: Starting handleCreateTenant');
      console.log('DEBUG: formData:', formData);
      console.log('DEBUG: formData.enabled_portals:', formData.enabled_portals);
      console.log('DEBUG: formData.enabled_services:', formData.enabled_services);
      
      // Check if all items are selected and show confirmation
      const totalPortals = availablePortals.length;
      const totalServices = availableServices.length;
      const selectedPortals = formData.enabled_portals?.length || 0;
      const selectedServices = formData.enabled_services?.length || 0;
      
      if (selectedPortals === totalPortals && selectedServices === totalServices) {
        const confirmAll = window.confirm(
          `Tüm portallar (${totalPortals}) ve servisler (${totalServices}) seçili. ` +
          `Bu, tüm Docker container'larını başlatacak. Devam etmek istiyor musunuz?`
        );
        if (!confirmAll) {
          return;
        }
      } else if (selectedPortals === totalPortals) {
        const confirmAllPortals = window.confirm(
          `Tüm portallar (${totalPortals}) seçili. Devam etmek istiyor musunuz?`
        );
        if (!confirmAllPortals) {
          return;
        }
      } else if (selectedServices === totalServices) {
        const confirmAllServices = window.confirm(
          `Tüm servisler (${totalServices}) seçili. Devam etmek istiyor musunuz?`
        );
        if (!confirmAllServices) {
          return;
        }
      }
      
      // Firma oluştur
      const newTenantResponse = await apiService.createTenant(formData);
      const createdTenantId = newTenantResponse?.tenant?.id ?? newTenantResponse?.id;
      
      // Tüm mevcut container'ları durdur
      const allContainers = [
        'astos-company-management-portal',
        'astos-b2b-portal', 
        'astos-b2c-portal',
        'astos-inventory-service',
        'astos-accounting-service',
        'astos-hr-service',
        'astos-crm-service',
        'astos-production-service',
        'astos-quality-service',
        'astos-maintenance-service',
        'astos-warehouse-service'
      ];
      
      // Not: Toplu durdurma endpoint'i mevcut değil. Gerekirse tek tek durdurma eklenebilir.
      
      // Seçili portallar ve servisler için Docker container'larını başlat
      const containersToStart = [];
      
      console.log('DEBUG: Processing portals and services...');
      console.log('DEBUG: enabled_portals type:', typeof formData.enabled_portals);
      console.log('DEBUG: enabled_portals length:', formData.enabled_portals ? formData.enabled_portals.length : 'null/undefined');
      console.log('DEBUG: enabled_services type:', typeof formData.enabled_services);
      console.log('DEBUG: enabled_services length:', formData.enabled_services ? formData.enabled_services.length : 'null/undefined');
      
      // Seçili portallar için container'ları ekle
      if (formData.enabled_portals && formData.enabled_portals.length > 0) {
        console.log('DEBUG: Processing enabled portals...');
        formData.enabled_portals.forEach(portalId => {
          console.log('DEBUG: Processing portal:', portalId);
          switch (portalId) {
            case 'company-management':
              containersToStart.push('astos-company-management-portal');
              break;
            case 'b2b-portal':
              containersToStart.push('astos-b2b-portal');
              break;
            case 'b2c-portal':
              containersToStart.push('astos-b2c-portal');
              break;
            // mobile-app için container yok, sadece web portal'lar
          }
        });
      } else {
        console.log('DEBUG: No portals selected or enabled_portals is empty');
      }
      
      // Seçili servisler için container'ları ekle
      if (formData.enabled_services && formData.enabled_services.length > 0) {
        console.log('DEBUG: Processing enabled services...');
        formData.enabled_services.forEach(serviceId => {
          console.log('DEBUG: Processing service:', serviceId);
          switch (serviceId) {
            case 'inventory':
              containersToStart.push('astos-inventory-service');
              break;
            case 'accounting':
              containersToStart.push('astos-accounting-service');
              break;
            case 'hr':
              containersToStart.push('astos-hr-service');
              break;
            case 'crm':
              containersToStart.push('astos-crm-service');
              break;
            case 'production':
              containersToStart.push('astos-production-service');
              break;
            case 'quality':
              containersToStart.push('astos-quality-service');
              break;
            case 'maintenance':
              containersToStart.push('astos-maintenance-service');
              break;
            case 'warehouse':
              containersToStart.push('astos-warehouse-service');
              break;
          }
        });
      } else {
        console.log('DEBUG: No services selected or enabled_services is empty');
      }
      
      console.log('DEBUG: Final containersToStart array:', containersToStart);
      console.log('DEBUG: containersToStart length:', containersToStart.length);
      
      // Önce Docker container'larını oluştur
      if (containersToStart.length > 0) {
        try {
          // Container konfigürasyonlarını hazırla
          const containerConfigs = [];
          
          // Tenant ID'ye göre port hesaplama
          const tenantId = createdTenantId;
          const basePortalPort = 3000 + (tenantId * 10); // Her tenant için 10 port aralığı
          const baseServicePort = 5000 + (tenantId * 10); // Her tenant için 10 port aralığı
          
          // Portal container'ları
          if (formData.enabled_portals?.includes('company-management')) {
            containerConfigs.push({
              name: 'company-management-portal',
              image: 'astos-company-management-portal:latest',
              port: basePortalPort + 1
            });
          }
          if (formData.enabled_portals?.includes('b2b-portal')) {
            containerConfigs.push({
              name: 'b2b-portal',
              image: 'astos-b2b-portal:latest',
              port: basePortalPort + 2
            });
          }
          if (formData.enabled_portals?.includes('b2c-portal')) {
            containerConfigs.push({
              name: 'b2c-portal',
              image: 'astos-b2c-portal:latest',
              port: basePortalPort + 3
            });
          }
          
          // Servis container'ları
          if (formData.enabled_services?.includes('inventory')) {
            containerConfigs.push({
              name: 'inventory-service',
              image: 'node:16-alpine',
              port: baseServicePort + 1
            });
          }
          if (formData.enabled_services?.includes('accounting')) {
            containerConfigs.push({
              name: 'accounting-service',
              image: 'node:16-alpine',
              port: baseServicePort + 2
            });
          }
          if (formData.enabled_services?.includes('hr')) {
            containerConfigs.push({
              name: 'hr-service',
              image: 'node:16-alpine',
              port: baseServicePort + 3
            });
          }
          if (formData.enabled_services?.includes('crm')) {
            containerConfigs.push({
              name: 'crm-service',
              image: 'node:16-alpine',
              port: baseServicePort + 4
            });
          }
          if (formData.enabled_services?.includes('production')) {
            containerConfigs.push({
              name: 'production-service',
              image: 'node:16-alpine',
              port: baseServicePort + 5
            });
          }
          if (formData.enabled_services?.includes('quality')) {
            containerConfigs.push({
              name: 'quality-service',
              image: 'node:16-alpine',
              port: baseServicePort + 6
            });
          }
          if (formData.enabled_services?.includes('maintenance')) {
            containerConfigs.push({
              name: 'maintenance-service',
              image: 'node:16-alpine',
              port: baseServicePort + 7
            });
          }
          if (formData.enabled_services?.includes('warehouse')) {
            containerConfigs.push({
              name: 'warehouse-service',
              image: 'node:16-alpine',
              port: baseServicePort + 8
            });
          }
          
          console.log('DEBUG: Creating containers with configs:', containerConfigs);
          
          // Docker containers are now created automatically during tenant creation
          console.log('Docker containers will be created automatically');
          
        } catch (dockerError) {
          console.error('Error creating Docker containers:', dockerError);
          // Docker hatası olsa bile firma oluşturma işlemi tamamlandı
        }
      } else {
        console.log('No containers selected to create');
      }
      
      setShowCreateForm(false);
      setFormData({
        name: '',
        domain: '',
        subdomain: '',
        // Firma Yönetici Bilgileri
        admin_username: '',
        admin_email: '',
        admin_password: '',
        admin_password_confirm: '',
        // Firma İletişim Bilgileri
        company_phone: '',
        company_address: '',
        tax_number: '',
        // B2B ve B2C Domain'leri
        b2b_domain: '',
        b2c_domain: '',
        // Servis ve Portal Seçimleri
        enabled_portals: [],
        enabled_services: []
      });
      loadTenants();
    } catch (error) {
      console.error('Error creating tenant:', error);
      setError('Firma oluşturulurken hata oluştu');
    }
  };

  const handleUpdateTenant = async (e) => {
    e.preventDefault();
    try {
      // Check if all items are selected and show confirmation
      const totalPortals = availablePortals.length;
      const totalServices = availableServices.length;
      const selectedPortals = formData.enabled_portals?.length || 0;
      const selectedServices = formData.enabled_services?.length || 0;
      
      if (selectedPortals === totalPortals && selectedServices === totalServices) {
        const confirmAll = window.confirm(
          `Tüm portallar (${totalPortals}) ve servisler (${totalServices}) seçili. ` +
          `Bu, tüm Docker container'larını başlatacak. Devam etmek istiyor musunuz?`
        );
        if (!confirmAll) {
          return;
        }
      } else if (selectedPortals === totalPortals) {
        const confirmAllPortals = window.confirm(
          `Tüm portallar (${totalPortals}) seçili. Devam etmek istiyor musunuz?`
        );
        if (!confirmAllPortals) {
          return;
        }
      } else if (selectedServices === totalServices) {
        const confirmAllServices = window.confirm(
          `Tüm servisler (${totalServices}) seçili. Devam etmek istiyor musunuz?`
        );
        if (!confirmAllServices) {
          return;
        }
      }
      
      // Firma güncelle
      const updatedTenant = await apiService.updateTenant(selectedTenant.id, formData);
      
      // Tüm mevcut container'ları durdur
      const allContainers = [
        'astos-company-management-portal',
        'astos-b2b-portal', 
        'astos-b2c-portal',
        'astos-inventory-service',
        'astos-accounting-service',
        'astos-hr-service',
        'astos-crm-service',
        'astos-production-service',
        'astos-quality-service',
        'astos-maintenance-service',
        'astos-warehouse-service'
      ];
      
      // Not: Toplu durdurma endpoint'i mevcut değil. Gerekirse tek tek durdurma eklenebilir.
      
      // Seçili portallar ve servisler için Docker container'larını başlat
      const containersToStart = [];
      
      // Seçili portallar için container'ları ekle
      if (formData.enabled_portals && formData.enabled_portals.length > 0) {
        formData.enabled_portals.forEach(portalId => {
          switch (portalId) {
            case 'company-management':
              containersToStart.push('astos-company-management-portal');
              break;
            case 'b2b-portal':
              containersToStart.push('astos-b2b-portal');
              break;
            case 'b2c-portal':
              containersToStart.push('astos-b2c-portal');
              break;
            // mobile-app için container yok, sadece web portal'lar
          }
        });
      }
      
      // Seçili servisler için container'ları ekle
      if (formData.enabled_services && formData.enabled_services.length > 0) {
        formData.enabled_services.forEach(serviceId => {
          switch (serviceId) {
            case 'inventory':
              containersToStart.push('astos-inventory-service');
              break;
            case 'accounting':
              containersToStart.push('astos-accounting-service');
              break;
            case 'hr':
              containersToStart.push('astos-hr-service');
              break;
            case 'crm':
              containersToStart.push('astos-crm-service');
              break;
            case 'production':
              containersToStart.push('astos-production-service');
              break;
            case 'quality':
              containersToStart.push('astos-quality-service');
              break;
            case 'maintenance':
              containersToStart.push('astos-maintenance-service');
              break;
            case 'warehouse':
              containersToStart.push('astos-warehouse-service');
              break;
          }
        });
      }
      
      // Sadece seçili Docker container'larını başlat
      if (containersToStart.length > 0) {
        try {
          await apiService.startTenantDockerContainers(selectedTenant.id, containersToStart);
          console.log('Selected Docker containers started successfully:', containersToStart);
        } catch (dockerError) {
          console.error('Error starting Docker containers:', dockerError);
          // Docker hatası olsa bile firma güncelleme işlemi tamamlandı
        }
      } else {
        console.log('No containers selected to start');
      }
      
      setShowEditForm(false);
      setSelectedTenant(null);
      setFormData({
        name: '',
        domain: '',
        subdomain: '',
        // Firma Yönetici Bilgileri
        admin_username: '',
        admin_email: '',
        admin_password: '',
        admin_password_confirm: '',
        // Firma İletişim Bilgileri
        company_phone: '',
        company_address: '',
        tax_number: '',
        // B2B ve B2C Domain'leri
        b2b_domain: '',
        b2c_domain: '',
        // Servis ve Portal Seçimleri
        enabled_portals: [],
        enabled_services: []
      });
      loadTenants();
    } catch (error) {
      console.error('Error updating tenant:', error);
      setError('Firma güncellenirken hata oluştu');
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (window.confirm('Bu firmayı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve tüm veriler silinecektir.')) {
      try {
        await apiService.deleteTenant(tenantId);
        loadTenants();
      } catch (error) {
        console.error('Error deleting tenant:', error);
        setError('Firma silinirken hata oluştu');
      }
    }
  };

  const handleToggleTenantStatus = async (tenantId, newStatus) => {
    const action = newStatus === 'active' ? 'aktif etmek' : 'pasif etmek';
    if (window.confirm(`Bu firmayı ${action} istediğinizden emin misiniz?`)) {
      try {
        if (newStatus === 'active') {
          await apiService.activateTenant(tenantId);
        } else {
          await apiService.deactivateTenant(tenantId);
        }
        loadTenants();
      } catch (error) {
        console.error('Error toggling tenant status:', error);
        setError(`Firma ${action} sırasında hata oluştu`);
      }
    }
  };

  const openEditForm = (tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name,
      domain: tenant.domain,
      email: tenant.email,
      phone: tenant.phone,
      address: tenant.address,
      tax_number: tenant.tax_number,
      subscription_plan: tenant.subscription_plan,
      max_users: tenant.max_users,
      max_storage_gb: tenant.max_storage_gb,
      enabled_portals: tenant.enabled_portals || [],
      enabled_services: tenant.enabled_services || []
    });
    setShowEditForm(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'red';
      case 'pending':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  // Portal ve servis seçimi yardımcı fonksiyonları
  const handlePortalToggle = (portalId) => {
    const currentPortals = formData.enabled_portals || [];
    const updatedPortals = currentPortals.includes(portalId)
      ? currentPortals.filter(id => id !== portalId)
      : [...currentPortals, portalId];
    
    setFormData({ ...formData, enabled_portals: updatedPortals });
  };

  const handleServiceToggle = (serviceId) => {
    const currentServices = formData.enabled_services || [];
    const updatedServices = currentServices.includes(serviceId)
      ? currentServices.filter(id => id !== serviceId)
      : [...currentServices, serviceId];
    
    setFormData({ ...formData, enabled_services: updatedServices });
  };

  // Select All / Deselect All functions
  const handleSelectAllPortals = () => {
    const allPortalIds = availablePortals.map(portal => portal.id);
    setFormData({ ...formData, enabled_portals: allPortalIds });
  };

  const handleDeselectAllPortals = () => {
    setFormData({ ...formData, enabled_portals: [] });
  };

  const handleSelectAllServices = () => {
    const allServiceIds = availableServices.map(service => service.id);
    setFormData({ ...formData, enabled_services: allServiceIds });
  };

  const handleDeselectAllServices = () => {
    setFormData({ ...formData, enabled_services: [] });
  };

  const isPortalSelected = (portalId) => {
    const result = (formData.enabled_portals || []).includes(portalId);
    return result;
  };

  const isServiceSelected = (serviceId) => {
    const result = (formData.enabled_services || []).includes(serviceId);
    return result;
  };

  // Get selected items for display
  const getSelectedPortals = () => {
    return availablePortals.filter(portal => isPortalSelected(portal.id));
  };

  const getSelectedServices = () => {
    return availableServices.filter(service => isServiceSelected(service.id));
  };

  // Kimlik doğrulama yükleniyorsa bekle
  if (authLoading) {
    return <div className="loading">Kimlik doğrulama kontrol ediliyor...</div>;
  }

  // Kullanıcı giriş yapmamışsa hata göster
  if (!isAuthenticated) {
    return <div className="error">Lütfen önce giriş yapın.</div>;
  }

  if (loading) {
    return <div className="loading">Yükleniyor...</div>;
  }

  if (error) {
    return (
      <div className="error">
        {error}
        <button onClick={loadTenants}>Tekrar Dene</button>
      </div>
    );
  }

  return (
    <div className="tenant-management">
      <div className="header">
        <h1>Firma Yönetimi</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateForm(true)}
        >
          Yeni Firma Ekle
        </button>
      </div>

      {/* Firma Listesi */}
      <div className="tenants-list">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Firma Adı</th>
              <th>Domain</th>
              <th>E-posta</th>
              <th>Telefon</th>
              <th>Durum</th>
              <th>Plan</th>
              <th>Kullanıcı Sayısı</th>
              <th>Entegrasyon Sayısı</th>
              <th>Oluşturulma Tarihi</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <tr key={tenant.id}>
                <td>{tenant.id}</td>
                <td>{tenant.name}</td>
                <td>{tenant.domain}</td>
                <td>{tenant.email}</td>
                <td>{tenant.phone}</td>
                <td>
                  <span className={`status-badge status-${getStatusColor(tenant.status)}`}>
                    {tenant.status}
                  </span>
                </td>
                <td>{tenant.subscription_plan}</td>
                <td>{tenant.user_count || 0}</td>
                <td>{tenant.integration_count || 0}</td>
                <td>{formatDate(tenant.created_at)}</td>
                <td>
                  <div className="actions">
                    <button 
                      className="btn btn-small btn-secondary"
                      onClick={() => openEditForm(tenant)}
                    >
                      Düzenle
                    </button>
                    <button 
                      className="btn btn-small btn-secondary"
                      onClick={() => handleToggleTenantStatus(tenant.id, tenant.status === 'active' ? 'inactive' : 'active')}
                    >
                      {tenant.status === 'active' ? 'Pasif Et' : 'Aktif Et'}
                    </button>
                    <button 
                      className="btn btn-small btn-danger"
                      onClick={() => handleDeleteTenant(tenant.id)}
                    >
                      Sil
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Yeni Firma Formu */}
      {showCreateForm && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Yeni Firma Ekle</h2>
              <button 
                className="close-btn"
                onClick={() => setShowCreateForm(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateTenant}>
              <div className="form-group">
                <label>Firma Adı *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Ana Domain *</label>
                <input
                  type="text"
                  value={formData.domain}
                  onChange={(e) => setFormData({...formData, domain: e.target.value})}
                  placeholder="örn: test1.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Alt Domain</label>
                <input
                  type="text"
                  value={formData.subdomain}
                  onChange={(e) => setFormData({...formData, subdomain: e.target.value})}
                  placeholder="örn: admin"
                />
              </div>
              
              {/* Firma Yönetici Bilgileri */}
              <div className="form-section">
                <h3>Firma Yönetici Bilgileri</h3>
                <div className="form-group">
                  <label>Yönetici Kullanıcı Adı *</label>
                  <input
                    type="text"
                    value={formData.admin_username}
                    onChange={(e) => setFormData({...formData, admin_username: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Yönetici E-posta *</label>
                  <input
                    type="email"
                    value={formData.admin_email}
                    onChange={(e) => setFormData({...formData, admin_email: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Yönetici Şifre *</label>
                  <input
                    type="password"
                    value={formData.admin_password}
                    onChange={(e) => setFormData({...formData, admin_password: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Yönetici Şifre Tekrar *</label>
                  <input
                    type="password"
                    value={formData.admin_password_confirm}
                    onChange={(e) => setFormData({...formData, admin_password_confirm: e.target.value})}
                    required
                  />
                </div>
              </div>
              
              {/* Firma İletişim Bilgileri */}
              <div className="form-section">
                <h3>Firma İletişim Bilgileri</h3>
                <div className="form-group">
                  <label>Firma Telefon</label>
                  <input
                    type="text"
                    value={formData.company_phone}
                    onChange={(e) => setFormData({...formData, company_phone: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Firma Adresi</label>
                  <textarea
                    value={formData.company_address}
                    onChange={(e) => setFormData({...formData, company_address: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Vergi Numarası</label>
                  <input
                    type="text"
                    value={formData.tax_number}
                    onChange={(e) => setFormData({...formData, tax_number: e.target.value})}
                  />
                </div>
              </div>
              
              {/* B2B ve B2C Domain'leri */}
              <div className="form-section">
                <h3>B2B ve B2C Domain'leri</h3>
                <div className="form-group">
                  <label>B2B Domain</label>
                  <input
                    type="text"
                    value={formData.b2b_domain}
                    onChange={(e) => setFormData({...formData, b2b_domain: e.target.value})}
                    placeholder="örn: b2b.test1.com"
                  />
                </div>
                <div className="form-group">
                  <label>B2C Domain</label>
                  <input
                    type="text"
                    value={formData.b2c_domain}
                    onChange={(e) => setFormData({...formData, b2c_domain: e.target.value})}
                    placeholder="örn: shop.test1.com"
                  />
                </div>
              </div>

              {/* Portal Seçimi */}
              <div className="form-group">
                <label>
                  Aktif Portallar 
                  <span className="selection-summary">
                    ({getSelectedPortals().length}/{availablePortals.length} seçili)
                  </span>
                </label>
                <div className="selection-controls">
                  <button 
                    type="button" 
                    className="btn btn-small btn-secondary"
                    onClick={handleSelectAllPortals}
                  >
                    Tümünü Seç
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-small btn-secondary"
                    onClick={handleDeselectAllPortals}
                  >
                    Tümünü Kaldır
                  </button>
                </div>
                <div className="checkbox-group">
                  {availablePortals.map((portal) => (
                    <label key={portal.id} className={`checkbox-item ${isPortalSelected(portal.id) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isPortalSelected(portal.id)}
                        onChange={() => handlePortalToggle(portal.id)}
                      />
                      <span className="checkbox-label">
                        <strong>{portal.name}</strong>
                        <small>{portal.description}</small>
                        {portal.port && <small>Port: {portal.port}</small>}
                      </span>
                    </label>
                  ))}
                </div>
                {getSelectedPortals().length > 0 && (
                  <div className="selected-items">
                    <strong>Seçili Portallar:</strong> {getSelectedPortals().map(p => p.name).join(', ')}
                  </div>
                )}
              </div>

              {/* Servis Seçimi */}
              <div className="form-group">
                <label>
                  Aktif Servisler 
                  <span className="selection-summary">
                    ({getSelectedServices().length}/{availableServices.length} seçili)
                  </span>
                </label>
                <div className="selection-controls">
                  <button 
                    type="button" 
                    className="btn btn-small btn-secondary"
                    onClick={handleSelectAllServices}
                  >
                    Tümünü Seç
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-small btn-secondary"
                    onClick={handleDeselectAllServices}
                  >
                    Tümünü Kaldır
                  </button>
                </div>
                <div className="checkbox-group">
                  {availableServices.map((service) => (
                    <label key={service.id} className={`checkbox-item ${isServiceSelected(service.id) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isServiceSelected(service.id)}
                        onChange={() => handleServiceToggle(service.id)}
                      />
                      <span className="checkbox-label">
                        <strong>{service.name}</strong>
                        <small>{service.description}</small>
                      </span>
                    </label>
                  ))}
                </div>
                {getSelectedServices().length > 0 && (
                  <div className="selected-items">
                    <strong>Seçili Servisler:</strong> {getSelectedServices().map(s => s.name).join(', ')}
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Firma Oluştur
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowCreateForm(false)}
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Düzenleme Formu */}
      {showEditForm && selectedTenant && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Firma Düzenle: {selectedTenant.name}</h2>
              <button 
                className="close-btn"
                onClick={() => setShowEditForm(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateTenant}>
              <div className="form-group">
                <label>Firma Adı *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Domain *</label>
                <input
                  type="text"
                  value={formData.domain}
                  onChange={(e) => setFormData({...formData, domain: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>E-posta</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Telefon</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Adres</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Vergi Numarası</label>
                <input
                  type="text"
                  value={formData.tax_number}
                  onChange={(e) => setFormData({...formData, tax_number: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Durum</label>
                <select
                  value={formData.status || selectedTenant.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                >
                  <option value="active">Aktif</option>
                  <option value="inactive">Pasif</option>
                  <option value="pending">Beklemede</option>
                </select>
              </div>
              <div className="form-group">
                <label>Abonelik Planı</label>
                <select
                  value={formData.subscription_plan}
                  onChange={(e) => setFormData({...formData, subscription_plan: e.target.value})}
                >
                  <option value="basic">Temel</option>
                  <option value="standard">Standart</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div className="form-group">
                <label>Maksimum Kullanıcı Sayısı</label>
                <input
                  type="number"
                  value={formData.max_users}
                  onChange={(e) => setFormData({...formData, max_users: parseInt(e.target.value)})}
                />
              </div>
                                           <div className="form-group">
                <label>Maksimum Depolama (GB)</label>
                <input
                  type="number"
                  value={formData.max_storage_gb}
                  onChange={(e) => setFormData({...formData, max_storage_gb: parseInt(e.target.value)})}
                />
              </div>

              {/* Port Yönetimi */}
              <div className="form-group">
                <label>Port Yönetimi</label>
                <div className="port-management">
                  <div className="port-item">
                    <label>Company Management Port:</label>
                    <input
                      type="number"
                      value={formData.company_port || (5100 + selectedTenant.id)}
                      onChange={(e) => setFormData({...formData, company_port: parseInt(e.target.value)})}
                      placeholder="5100"
                    />
                  </div>
                  <div className="port-item">
                    <label>B2B Portal Port:</label>
                    <input
                      type="number"
                      value={formData.b2b_port || (6100 + selectedTenant.id)}
                      onChange={(e) => setFormData({...formData, b2b_port: parseInt(e.target.value)})}
                      placeholder="6100"
                    />
                  </div>
                  <div className="port-item">
                    <label>B2C Portal Port:</label>
                    <input
                      type="number"
                      value={formData.b2c_port || (7100 + selectedTenant.id)}
                      onChange={(e) => setFormData({...formData, b2c_port: parseInt(e.target.value)})}
                      placeholder="7100"
                    />
                  </div>
                </div>
              </div>

              {/* Domain Yönetimi */}
              <div className="form-group">
                <label>Domain Yönetimi</label>
                <div className="domain-management">
                  <div className="domain-item">
                    <label>Ana Domain:</label>
                    <input
                      type="text"
                      value={formData.domain}
                      onChange={(e) => setFormData({...formData, domain: e.target.value})}
                      placeholder="firma.com"
                    />
                  </div>
                  <div className="domain-item">
                    <label>B2B Subdomain:</label>
                    <input
                      type="text"
                      value={formData.b2b_domain || `b2b.${formData.domain}`}
                      onChange={(e) => setFormData({...formData, b2b_domain: e.target.value})}
                      placeholder="b2b.firma.com"
                    />
                  </div>
                  <div className="domain-item">
                    <label>B2C Subdomain:</label>
                    <input
                      type="text"
                      value={formData.b2c_domain || `b2c.${formData.domain}`}
                      onChange={(e) => setFormData({...formData, b2c_domain: e.target.value})}
                      placeholder="b2c.firma.com"
                    />
                  </div>
                </div>
              </div>

               {/* Portal Seçimi */}
               <div className="form-group">
                 <label>Aktif Portallar</label>
                 <div className="checkbox-group">
                   {availablePortals.map((portal) => (
                     <label key={portal.id} className="checkbox-item">
                       <input
                         type="checkbox"
                         checked={isPortalSelected(portal.id)}
                         onChange={() => handlePortalToggle(portal.id)}
                       />
                       <span className="checkbox-label">
                         <strong>{portal.name}</strong>
                         <small>{portal.description}</small>
                         {portal.port && <small>Port: {portal.port}</small>}
                       </span>
                     </label>
                   ))}
                 </div>
               </div>

               {/* Servis Seçimi */}
               <div className="form-group">
                 <label>Aktif Servisler</label>
                 <div className="checkbox-group">
                   {availableServices.map((service) => (
                     <label key={service.id} className="checkbox-item">
                       <input
                         type="checkbox"
                         checked={isServiceSelected(service.id)}
                         onChange={() => handleServiceToggle(service.id)}
                       />
                       <span className="checkbox-label">
                         <strong>{service.name}</strong>
                         <small>{service.description}</small>
                       </span>
                     </label>
                   ))}
                 </div>
               </div>

               <div className="form-actions">
                 <button type="submit" className="btn btn-primary">
                   Güncelle
                 </button>
                 <button 
                   type="button" 
                   className="btn btn-secondary"
                   onClick={() => setShowEditForm(false)}
                 >
                   İptal
                 </button>
               </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;
